import React, { createContext, useContext, useState, useCallback } from 'react';

const HotelBookingContext = createContext();

export function HotelBookingProvider({ children }) {
  // Tracks the currently active option (0, 1, or 2)
  const [activeOption, setActiveOption] = useState(0);
  // Tracks if each option is complete
  const [optionStatus, setOptionStatus] = useState([false, false, false]);
  const [hotelInfo, setHotelInfo] = useState(null);

  // Functions to navigate between options
  const goNext = useCallback(
    () => setActiveOption(prev => Math.min(prev + 1, 2)),
    [],
  );
  const goPrev = useCallback(
    () => setActiveOption(prev => Math.max(prev - 1, 0)),
    [],
  );

  const setOptionComplete = useCallback((index, value = true) => {
    setOptionStatus(prev => {
      const updated = [...prev];
      updated[index] = !!value;
      return updated;
    });
  }, []);

  // Marks an option as complete and moves to the next one
  const markOptionComplete = useCallback(index => {
    setOptionStatus(prev => {
      const updated = [...prev];
      updated[index] = true;
      return updated;
    });
  }, []);

  // Context value to be provided to children components
  const contextValue = React.useMemo(
    () => ({
      activeOption,
      setActiveOption,
      optionStatus,
      markOptionComplete,
      goNext,
      goPrev,
      hotelInfo,
      setHotelInfo,
      setOptionComplete,
    }),
    [
      activeOption,
      optionStatus,
      hotelInfo,
      setHotelInfo,
      markOptionComplete,
      goNext,
      goPrev,
      setOptionComplete,
    ],
  );

  return (
    <HotelBookingContext.Provider value={contextValue}>
      {children}
    </HotelBookingContext.Provider>
  );
}

export const useHotelBooking = () => {
  const ctx = useContext(HotelBookingContext);
  if (!ctx) {
    throw new Error('useHotelBooking must be used within HotelBookingProvider');
  }
  return ctx;
};
